import React, { useState } from 'react';
import {
  Box, Button, Typography, Paper, Divider, Chip, Tabs, Tab,
  Avatar, Collapse, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PublicIcon from '@mui/icons-material/Public';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WorkOutlinedIcon from '@mui/icons-material/WorkOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TranslateIcon from '@mui/icons-material/Translate';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CandidateCard from './CandidateCard';
import { AnalysisResponse, CandidateResult } from '../types';

interface Props {
  data: AnalysisResponse;
  jobLabel: string;
  apiBase: string;
  onBack: () => void;
  isDark: boolean;
}

function exportCSV(candidates: CandidateResult[], jobLabel: string) {
  const headers = ['Rank', 'Name', 'Email', 'Phone', 'Match Score', 'Nationality', 'Years Exp', 'Current Role', 'Top Skills', 'Strengths', 'Gaps', 'Summary'];
  const rows = candidates.map((c, i) => [
    i + 1, c.name, c.email || '—', c.phone || '—',
    c.matchScore !== null ? `${c.matchScore}%` : 'N/A',
    c.nationality, c.yearsExperience, c.currentRole,
    c.topSkills.join('; '), c.strengths.join('; '), c.gaps.join('; '), c.summary,
  ]);
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'shortlist-report.csv'; a.click();
  URL.revokeObjectURL(url);
}

async function exportPDF(candidates: CandidateResult[], poolStats: AnalysisResponse['poolStats'], jobLabel: string, apiBase: string) {
  const res = await fetch(`${apiBase}/api/shortlist/export-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ candidates, poolStats, jobLabel }),
  });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'shortlist-report.pdf'; a.click();
  URL.revokeObjectURL(url);
}

function getScoreColor(score: number | null) {
  if (score === null) return '#94A3B8';
  if (score >= 75) return '#10B981';
  if (score >= 50) return '#FF8428';
  if (score >= 25) return '#F59E0B';
  return '#EF4444';
}

const NATIONALITY_FLAGS: Record<string, string> = {
  Egyptian: '🇪🇬', Indian: '🇮🇳', Filipino: '🇵🇭', Pakistani: '🇵🇰',
  Emirati: '🇦🇪', British: '🇬🇧', Lebanese: '🇱🇧', Jordanian: '🇯🇴',
  Syrian: '🇸🇾', Bangladeshi: '🇧🇩', Sudanese: '🇸🇩', Ethiopian: '🇪🇹',
  Nigerian: '🇳🇬', 'South African': '🇿🇦', American: '🇺🇸', Saudi: '🇸🇦',
  Iraqi: '🇮🇶', Iranian: '🇮🇷', Turkish: '🇹🇷', 'Sri Lankan': '🇱🇰',
  Nepali: '🇳🇵', Moroccan: '🇲🇦', Tunisian: '🇹🇳', Algerian: '🇩🇿',
  Canadian: '🇨🇦', Australian: '🇦🇺', German: '🇩🇪', French: '🇫🇷',
  Spanish: '🇪🇸', Italian: '🇮🇹', Chinese: '🇨🇳', Indonesian: '🇮🇩',
  Malaysian: '🇲🇾', Kenyan: '🇰🇪', Ugandan: '🇺🇬', Ghanaian: '🇬🇭',
  Kuwaiti: '🇰🇼', Bahraini: '🇧🇭', Qatari: '🇶🇦', Omani: '🇴🇲',
  Yemeni: '🇾🇪', Libyan: '🇱🇾', Russian: '🇷🇺', Ukrainian: '🇺🇦',
  Portuguese: '🇵🇹', Dutch: '🇳🇱', Belgian: '🇧🇪', Japanese: '🇯🇵',
};

const glassPanel = (isDark: boolean) => ({
  p: 2.5, borderRadius: 3,
  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.72)',
  border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(255,255,255,0.9)',
  backdropFilter: 'blur(16px)',
});

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; isDark: boolean }> = ({ icon, label, value, isDark }) => (
  <Paper elevation={0} sx={{
    ...glassPanel(isDark),
    flex: 1, minWidth: 110, p: 2, textAlign: 'center',
  }}>
    <Box sx={{ color: 'primary.main', mb: 0.5 }}>{icon}</Box>
    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.03em', color: 'text.primary', lineHeight: 1 }}>{value}</Typography>
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, fontSize: '0.7rem' }}>{label}</Typography>
  </Paper>
);

// ── Tab panels ──────────────────────────────────────────────────────────────

const CandidatesPanel: React.FC<{ shortlisted: CandidateResult[]; strays: CandidateResult[] }> = ({ shortlisted, strays }) => (
  <Box>
    {shortlisted.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>Shortlisted</Typography>
          <Chip label={shortlisted.length} size="small" sx={{ height: 20, fontSize: '0.72rem', fontWeight: 700 }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {shortlisted.map((c, i) => <CandidateCard key={c.filename} candidate={c} rank={i + 1} />)}
        </Box>
      </Box>
    )}
    {strays.length > 0 && (
      <Box>
        <Divider sx={{ mb: 2.5 }}>
          <Chip icon={<WarningAmberIcon />} label={`${strays.length} Stray${strays.length > 1 ? 's' : ''} — Low match`} size="small"
            sx={{ fontWeight: 700, background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }} />
        </Divider>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {strays.map((c, i) => <CandidateCard key={c.filename} candidate={c} rank={shortlisted.length + i + 1} />)}
        </Box>
      </Box>
    )}
  </Box>
);

const NationalitiesPanel: React.FC<{ candidates: CandidateResult[]; isDark: boolean }> = ({ candidates, isDark }) => {
  // Group candidates by nationality (excluding Unknown / —)
  const groups: Record<string, CandidateResult[]> = {};
  candidates.forEach(c => {
    if (c.nationality && c.nationality !== 'Unknown' && c.nationality !== '—') {
      (groups[c.nationality] ||= []).push(c);
    }
  });
  const sorted = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  const total = sorted.reduce((s, [, arr]) => s + arr.length, 0);
  if (sorted.length === 0) return <Typography color="text.secondary">No nationality data available.</Typography>;

  return (
    <Paper elevation={0} sx={glassPanel(isDark)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sorted.map(([nat, arr]) => {
          const pct = Math.round((arr.length / total) * 100);
          return (
            <Box key={nat}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: '1.4rem', width: 28, flexShrink: 0, lineHeight: 1 }}>{NATIONALITY_FLAGS[nat] || '🌍'}</Typography>
                <Typography sx={{ width: 130, fontSize: '0.85rem', fontWeight: 700, color: 'text.primary', flexShrink: 0 }}>{nat}</Typography>
                <Box sx={{ flex: 1, height: 10, borderRadius: 5, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 5, background: 'linear-gradient(90deg, #FF8428, #FFB06A)', transition: 'width 0.6s ease' }} />
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', width: 56, textAlign: 'right' }}>
                  {arr.length} <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>({pct}%)</Box>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 5 }}>
                {arr.map(c => (
                  <Chip
                    key={c.filename}
                    label={
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {c.name}
                        {c.matchScore !== null && (
                          <Box component="span" sx={{ color: getScoreColor(c.matchScore), fontWeight: 800 }}>· {c.matchScore}%</Box>
                        )}
                      </Box>
                    }
                    size="small"
                    sx={{
                      height: 22, fontSize: '0.72rem', fontWeight: 600,
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                      opacity: c.isStray ? 0.55 : 1,
                    }}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// Known languages — anything matching goes into Languages bucket, not Skills
const LANGUAGE_SET = new Set([
  'english', 'arabic', 'french', 'spanish', 'german', 'italian', 'portuguese',
  'russian', 'ukrainian', 'polish', 'dutch', 'mandarin', 'chinese', 'cantonese',
  'japanese', 'korean', 'hindi', 'urdu', 'malayalam', 'tamil', 'telugu',
  'bengali', 'punjabi', 'filipino', 'tagalog', 'thai', 'vietnamese',
  'indonesian', 'malay', 'turkish', 'farsi', 'persian', 'hebrew', 'greek',
  'swahili', 'hausa', 'yoruba', 'igbo', 'amharic', 'twi',
]);

// Normalize a skill string for grouping
function normalizeSkill(raw: string): { key: string; display: string; isLanguage: boolean } {
  let s = raw.trim();
  // Extract & strip parenthetical qualifier like "(fluent)" or "(basic)"
  s = s.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  // "CRM (Salesforce)" already handled above; also handle "Salesforce CRM" → Salesforce
  s = s.replace(/\s+/g, ' ');
  const lowered = s.toLowerCase();
  const isLanguage = LANGUAGE_SET.has(lowered);
  // Title-case for display, preserve acronyms
  const display = s
    .split(' ')
    .map(w => (/^[A-Z0-9]{2,}$/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
  return { key: lowered, display, isLanguage };
}

const SkillsPanel: React.FC<{ candidates: CandidateResult[]; isDark: boolean }> = ({ candidates, isDark }) => {
  const skillFreq: Record<string, { display: string; count: number }> = {};
  const langFreq: Record<string, { display: string; count: number }> = {};

  candidates.forEach(c => {
    const seenThisCV = new Set<string>();
    c.topSkills.forEach(rawSkill => {
      if (!rawSkill) return;
      const { key, display, isLanguage } = normalizeSkill(rawSkill);
      if (!key || seenThisCV.has(key)) return; // dedupe per candidate
      seenThisCV.add(key);
      const bucket = isLanguage ? langFreq : skillFreq;
      if (bucket[key]) bucket[key].count += 1;
      else bucket[key] = { display, count: 1 };
    });
  });

  const skills = Object.values(skillFreq).sort((a, b) => b.count - a.count);
  const languages = Object.values(langFreq).sort((a, b) => b.count - a.count);
  const maxSkill = skills[0]?.count || 1;
  const maxLang = languages[0]?.count || 1;

  const renderBar = (display: string, count: number, max: number) => (
    <Box key={display} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={{ width: 180, fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>{display}</Typography>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', width: `${(count / max) * 100}%`, borderRadius: 4,
          background: count >= 5 ? 'linear-gradient(90deg, #10B981, #34D399)'
                    : count >= 3 ? 'linear-gradient(90deg, #FF8428, #FFB06A)'
                    : count >= 2 ? 'linear-gradient(90deg, #F59E0B, #FCD34D)'
                    : 'rgba(148,163,184,0.5)',
          transition: 'width 0.6s ease',
        }} />
      </Box>
      <Chip label={`${count}`} size="small"
        sx={{ fontSize: '0.72rem', height: 20, fontWeight: 700, minWidth: 32, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }} />
    </Box>
  );

  if (skills.length === 0 && languages.length === 0) {
    return <Typography color="text.secondary">No skill data available.</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {skills.length > 0 && (
        <Paper elevation={0} sx={glassPanel(isDark)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BuildOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
              Skills · {skills.length} unique across {candidates.length} candidates
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {skills.map(s => renderBar(s.display, s.count, maxSkill))}
          </Box>
        </Paper>
      )}
      {languages.length > 0 && (
        <Paper elevation={0} sx={glassPanel(isDark)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TranslateIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
              Languages
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {languages.map(l => renderBar(l.display, l.count, maxLang))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const SummariesPanel: React.FC<{ candidates: CandidateResult[]; isDark: boolean }> = ({ candidates, isDark }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {candidates.map((c, i) => (
      <Paper key={c.filename} elevation={0} sx={{ ...glassPanel(isDark), display: 'flex', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%',
            border: `2.5px solid ${getScoreColor(c.matchScore)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.9)',
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: getScoreColor(c.matchScore) }}>
              {c.matchScore !== null ? c.matchScore : '—'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontWeight: 600 }}>#{i + 1}</Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'text.primary' }}>{c.name}</Typography>
            {c.nationality && c.nationality !== 'Unknown' && (
              <Typography sx={{ fontSize: '1rem' }}>{NATIONALITY_FLAGS[c.nationality] || '🌍'}</Typography>
            )}
            {c.currentRole && c.currentRole !== '—' && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>· {c.currentRole}</Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.83rem' }}>
            {c.summary || 'No summary available.'}
          </Typography>
        </Box>
      </Paper>
    ))}
  </Box>
);

const InfoCard: React.FC<{ c: CandidateResult; rank: number; isDark: boolean }> = ({ c, rank, isDark }) => {
  const [open, setOpen] = useState(false);
  const hasSummary = c.summary && c.summary.trim().length > 0;
  return (
    <Paper elevation={0} sx={{ ...glassPanel(isDark), p: 0 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', p: 2.5 }}>
        <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(135deg, #FF8428, #FFB06A)', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
          {c.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'text.primary' }}>{c.name}</Typography>
            <Chip label={`#${rank}`} size="small" sx={{ height: 18, fontSize: '0.68rem', fontWeight: 700 }} />
            {c.matchScore !== null && (
              <Chip label={`${c.matchScore}%`} size="small"
                sx={{ height: 18, fontSize: '0.68rem', fontWeight: 800, color: getScoreColor(c.matchScore), background: `${getScoreColor(c.matchScore)}18`, border: `1px solid ${getScoreColor(c.matchScore)}30` }} />
            )}
          </Box>
          {c.currentRole && c.currentRole !== '—' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
              <WorkOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.currentRole}</Typography>
              {c.yearsExperience && c.yearsExperience !== '—' && (
                <>
                  <AccessTimeIcon sx={{ fontSize: 12, color: 'text.secondary', ml: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.yearsExperience} yrs</Typography>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {c.email && c.email !== '—' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EmailOutlinedIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>{c.email}</Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No email found</Typography>
          )}
          {c.phone && c.phone !== '—' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>{c.phone}</Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No phone found</Typography>
          )}
        </Box>
        {hasSummary && (
          <IconButton
            size="small"
            onClick={() => setOpen(o => !o)}
            aria-label="Toggle summary"
            sx={{
              color: 'text.secondary',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {hasSummary && (
        <Collapse in={open}>
          <Box sx={{
            px: 2.5, pb: 2.5,
            borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
            pt: 1.5,
          }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.75, display: 'block' }}>
              Summary
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.84rem' }}>
              {c.summary}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

const InfoPanel: React.FC<{ candidates: CandidateResult[]; isDark: boolean }> = ({ candidates, isDark }) => {
  const withContact = candidates.filter(c => !c.isStray);
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 2, display: 'block' }}>
        Contact info — shortlisted candidates only · click the chevron to see summary
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {withContact.map((c, i) => (
          <InfoCard key={c.filename} c={c} rank={i + 1} isDark={isDark} />
        ))}
      </Box>
    </Box>
  );
};

// ── Main Dashboard ───────────────────────────────────────────────────────────

const Dashboard: React.FC<Props> = ({ data, jobLabel, apiBase, onBack, isDark }) => {
  const { candidates, poolStats } = data;
  const [tab, setTab] = useState(0);

  const shortlisted = candidates.filter(c => !c.isStray);
  const strays = candidates.filter(c => c.isStray);

  const tabSx = {
    fontWeight: 700, fontSize: '0.8rem', textTransform: 'none',
    minHeight: 40, px: 2,
  };

  return (
    <Box className="fade-up">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>New analysis</Button>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1.2 }}>Results</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{poolStats.totalCandidates} CVs · {jobLabel}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => exportCSV(candidates, jobLabel)}>CSV</Button>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => exportPDF(candidates, poolStats, jobLabel, apiBase)}>PDF Report</Button>
        </Box>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <StatCard icon={<PeopleOutlinedIcon />} label="Total CVs" value={poolStats.totalCandidates} isDark={isDark} />
        {poolStats.avgMatchScore !== null && <StatCard icon={<LeaderboardIcon />} label="Avg Match" value={`${poolStats.avgMatchScore}%`} isDark={isDark} />}
        {poolStats.topMatch && <StatCard icon={<EmojiEventsIcon />} label="Top Candidate" value={poolStats.topMatch.split(' ')[0]} isDark={isDark} />}
        <StatCard icon={<PublicIcon />} label="Nationalities" value={Object.keys(poolStats.nationalityBreakdown).length} isDark={isDark} />
        {strays.length > 0 && <StatCard icon={<WarningAmberIcon />} label="Strays" value={strays.length} isDark={isDark} />}
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ ...glassPanel(isDark), mb: 2, p: 0.5 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': { background: 'linear-gradient(90deg, #FF8428, #FFB06A)', borderRadius: 2 },
          }}
        >
          <Tab label="Candidates" sx={tabSx} />
          <Tab label="Nationalities" sx={tabSx} />
          <Tab label="Skills" sx={tabSx} />
          <Tab label="Summaries" sx={tabSx} />
          <Tab label="Info" sx={tabSx} />
        </Tabs>
      </Paper>

      {/* Tab content */}
      <Box>
        {tab === 0 && <CandidatesPanel shortlisted={shortlisted} strays={strays} />}
        {tab === 1 && <NationalitiesPanel candidates={candidates} isDark={isDark} />}
        {tab === 2 && <SkillsPanel candidates={candidates} isDark={isDark} />}
        {tab === 3 && <SummariesPanel candidates={candidates} isDark={isDark} />}
        {tab === 4 && <InfoPanel candidates={candidates} isDark={isDark} />}
      </Box>
    </Box>
  );
};

export default Dashboard;
